import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Phone, Mail, MapPin, Clock, Instagram, Facebook, Twitter } from 'lucide-react';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    guestCount: '',
    eventDate: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock form submission
    toast({
      title: 'Quote Request Received!',
      description: 'Thank you! We\'ll get back to you within 24 hours with a personalized quote.',
    });

    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      eventType: '',
      guestCount: '',
      eventDate: '',
      message: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      content: '0161 222 8831',
      link: 'tel:01612228831'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'info@marlowescatering.co.uk',
      link: 'mailto:info@marlowescatering.co.uk'
    },
    {
      icon: MapPin,
      title: 'Location',
      content: 'Chadderton, Oldham OL9',
      link: null
    },
    {
      icon: Clock,
      title: 'Hours',
      content: 'Mon-Sun: 8am - 8pm',
      link: null
    },
    {
      icon: Instagram,
      title: 'Instagram',
      content: '@marlowes_chad',
      link: 'https://www.instagram.com/marlowes_chad/'
    },
    {
      icon: Facebook,
      title: 'Facebook',
      content: 'Marlowes Catering',
      link: 'https://www.facebook.com/share/17sk47SFGE/?mibextid=wwXIfr'
    },
    {
      icon: Twitter,
      title: 'Twitter',
      content: '@marlowes_chad',
      link: 'https://twitter.com/marlowes_chad'
    }
  ];

  return (
    <section id="contact" className="py-20" style={{ backgroundColor: 'var(--color-accent-coconut)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: 'var(--text-base)' }}>
            Get in Touch
          </h2>
          <p className="text-xl max-w-3xl mx-auto" style={{ color: 'var(--text-subtle-light)' }}>
            Ready to make your event unforgettable? Contact us for a personalized quote.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h3 className="text-3xl font-bold mb-6" style={{ color: 'var(--text-base)' }}>
              Contact Information
            </h3>
            <p className="mb-8 text-lg" style={{ color: 'var(--text-subtle-light)' }}>
              Get in touch today for a free, no-obligation quote. We typically respond within 24 hours.
            </p>

            <div className="space-y-6">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                const content = info.link ? (
                  <a
                    href={info.link}
                    target={info.link.startsWith('http') ? '_blank' : undefined}
                    rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="hover:opacity-70 transition-opacity"
                  >
                    {info.content}
                  </a>
                ) : (
                  info.content
                );

                return (
                  <div key={index} className="flex items-start space-x-4">
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'white' }}
                    >
                      <Icon size={24} style={{ color: 'var(--accent-ui-accent)' }} />
                    </div>
                    <div>
                      <p className="font-semibold mb-1" style={{ color: 'var(--text-base)' }}>
                        {info.title}
                      </p>
                      <p style={{ color: 'var(--text-subtle-light)' }}>
                        {content}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 p-6 bg-white rounded-xl">
              <h4 className="font-bold text-lg mb-2" style={{ color: 'var(--text-base)' }}>
                Service Area
              </h4>
              <p style={{ color: 'var(--text-subtle-light)' }}>
                We serve Chadderton, Oldham, Rochdale, Manchester, and surrounding areas in Greater Manchester. Free local delivery available.
              </p>
            </div>
          </div>

          {/* Quote Request Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h3 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-base)' }}>
              Request a Quote
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your name"
                  className="mt-1"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="07xxx xxxxxx"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="eventType">Event Type *</Label>
                <Select
                  value={formData.eventType}
                  onValueChange={(value) => setFormData({ ...formData, eventType: value })}
                  required
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="corporate">Corporate Event</SelectItem>
                    <SelectItem value="party">Birthday/Party</SelectItem>
                    <SelectItem value="funeral">Funeral/Wake</SelectItem>
                    <SelectItem value="festival">Festival</SelectItem>
                    <SelectItem value="matchday">Matchday Catering</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="guestCount">Guest Count *</Label>
                  <Input
                    id="guestCount"
                    name="guestCount"
                    type="number"
                    value={formData.guestCount}
                    onChange={handleChange}
                    required
                    placeholder="Number of guests"
                    className="mt-1"
                    min="20"
                  />
                </div>
                <div>
                  <Label htmlFor="eventDate">Event Date</Label>
                  <Input
                    id="eventDate"
                    name="eventDate"
                    type="date"
                    value={formData.eventDate}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="message">Additional Details</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your event, dietary requirements, venue details, etc."
                  className="mt-1"
                  rows={4}
                />
              </div>

              <Button
                type="submit"
                className="w-full text-lg py-6"
                style={{ backgroundColor: 'var(--accent-ui-accent)', color: 'white' }}
              >
                Request Quote
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;